import { FormikErrors, FormikTouched } from 'formik';
import { TelephoneCountryCodesEnum } from '../../../resources/base/models';
import { Analytics } from '../../../services/analytics';
import { formErrorEvent } from '../../../services/analytics/analyticsEvents';
import { UpdateExistingBusinessRequest, UpdateExistingCustomerRequest } from '../../../services/api/miles/types';
import { formatPhoneNumber, removeLeadingZerosFromMobilePhone } from '../../../services/common/format';
import { MilesCustomerIdentifierSourceType } from '../../../services/common/miles-pos-quote-integration/models';
import { getIsSoleTrader } from '../../../services/common/miscUtils';
import { genderToSalutation } from '../utils/checkGenderUtil';
import { uiDateToApiDate } from '../utils/formatDate';
import {
  BusinessCustomerProspectForm,
  PrivateCoCustomerProspectForm,
  PrivateCustomerProspectForm,
} from './initialValues';

export const getCreateOrUpdateCustomerRequest = (
  values: PrivateCustomerProspectForm,
  milesCustomerId: string | undefined
): UpdateExistingCustomerRequest => {
  const financialProduct: UpdateExistingCustomerRequest['financialProduct'] = {
    offers: [
      {
        previousContractId: values.previousContractNumber!,
      },
    ],
  };

  return {
    customerData: {
      milesCustomerId,
      salutation: genderToSalutation(values.gender!),
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: uiDateToApiDate(values.dateOfBirth),
      gender: values.gender,
      birthName: values.birthName,
      profession: values.profession,
      contactData: {
        mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone),
        mobilePhoneCountryCode: values.mobilePhoneCountryCode,
        telephoneNumber: formatPhoneNumber(values.telephonePhone),
        telephoneCountryCode: values.telephonePhone ? TelephoneCountryCodesEnum['+33'] : undefined,
        email: values.email!,
      },
    },
    ...(values.previousContractNumber && { financialProduct }),
  };
};

export const getCreateCoCustomerRequest = (
  values: PrivateCoCustomerProspectForm,
  milesCustomerId: string | undefined
): UpdateExistingCustomerRequest => {
  return {
    customerData: {
      milesCustomerId,
      salutation: genderToSalutation(values.gender!),
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: uiDateToApiDate(values.dateOfBirth),
      gender: values.gender,
      birthName: values.birthName,
      customerRelations: values.customerRelations,
      profession: values.profession,
      contactData: {
        mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone),
        mobilePhoneCountryCode: values.mobilePhoneCountryCode,
        telephoneNumber: formatPhoneNumber(values.telephonePhone),
        telephoneCountryCode: values.telephonePhone ? TelephoneCountryCodesEnum['+33'] : undefined,
        email: values.email!,
      },
    },
  };
};

export const getCreateOrUpdateBusinessCustomerRequest = (
  values: BusinessCustomerProspectForm,
  milesCustomerId: string | undefined
): UpdateExistingBusinessRequest => {
  const financialProduct: UpdateExistingBusinessRequest['financialProduct'] = {
    offers: [
      {
        previousContractId: values.previousContractNumber!,
      },
    ],
  };

  const soleTraderData = getIsSoleTrader(values.legalEntity)
    ? {
        firstName: values.firstName,
        lastName: values.lastName,
        // birthName: values.birthName, // TODO: uncomment when supported by BFT
        gender: values.gender,
        salutation: genderToSalutation(values.gender!),
        dateOfBirth: uiDateToApiDate(values.dateOfBirth),
      }
    : {};

  return {
    milesCustomerId,
    companyData: {
      entityLegalForm: values.legalEntity,
      name: values.companyName,
      foundationDate: uiDateToApiDate(values.companyCreationDate),
      companyRegistrationNumber: String(values.sirenWebserviceCall),
      apecode: values.apecode,
      privateUserCheck: values.privateUserCheck,
      contactData: {
        mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone),
        mobilePhoneCountryCode: values.mobilePhoneCountryCode,
        telephoneNumber: formatPhoneNumber(values.telephonePhone),
        telephoneCountryCode: values.telephonePhone ? TelephoneCountryCodesEnum['+33'] : undefined,
        email: values.email!,
      },
    },
    ...(values.previousContractNumber && { financialProduct }),
    ...soleTraderData,
  };
};

export const sendValidationErrors = (errors: FormikErrors<any>, touched: FormikTouched<any>) => {
  const errorsToSend: string[] = [];
  Object.keys(errors).forEach((field) => {
    if (touched[field]) {
      errorsToSend.push(field);
    }
  });
  if (errorsToSend.length > 0) {
    Analytics.push(formErrorEvent);
  }
};

export const getIsCoCustomerLinkedToQuote = (
  customerData: QuoteSchema.Schemas.GetCoCustomersCustomerData | undefined,
  milesCustomerId: string
): boolean => {
  if (!customerData) {
    return false;
  }

  return (
    customerData.customerRelations?.some((relation) =>
      relation?.externalIdentifiers?.some(
        (identifier) =>
          identifier.type === MilesCustomerIdentifierSourceType.MILES_CUSTOMER_ID && identifier.id === milesCustomerId
      )
    ) ?? false
  );
};
